import { Component, OnInit, signal, WritableSignal, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RecruitingService } from 'src/app/api/recruiting.service';
import { LocationQuickNote } from 'src/app/api/types/recruiting';
import { UiModule } from 'src/app/ui/ui.module';
import { TableColumnComponent } from 'src/app/ui/tables/table-column/table-column.component';
import { TableHeaderComponent } from 'src/app/ui/tables/table-header/table-header.component';
import { Column } from 'src/app/ui/tables';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { IconService } from 'src/app/ui/icon.service';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { LinkableLocationsService } from 'src/app/api/linkable-locations.service';
import { EditLocationQuicknotesComponent, EditLocationQuicknotesComponentInputs } from '../edit-location-quicknotes/edit-location-quicknotes.component';

@Component({
  selector: 'app-location-quicknotes',
  templateUrl: './location-quicknotes.component.html',
  styleUrls: ['./location-quicknotes.component.scss'],
  standalone: true,
  imports: [CommonModule, UiModule, TableColumnComponent, TableHeaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationQuicknotesComponent implements OnInit {
  loading = false;
  error: string | null = null;
  quickNotes = signal<LocationQuickNote[]>([]);
  showEditButton = false;

  columns: WritableSignal<Column<any>[]> = signal([
    { field: 'name', header: 'Location Name', type: 'text', sortable: true, filterable: true },
    { field: 'domestic', header: 'Domestic', type: 'text', sortable: true, filterable: true },
    { field: 'recruitingQuicknote', header: 'Notes', type: 'html', sortable: true, filterable: true }
  ]);

  constructor(
    private recruitingService: RecruitingService,
    private auth: LocalAuthService,
    public icons: IconService,
    private dataPadService: DataPadService,
    private locationService: LinkableLocationsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.loadQuickNotes();
    const currentUser = this.auth.currentUser;

    // Special users who get edit access to all locations
    const specialUserIds = [26, 418, 144, 6, 106, 17, 135];

    if (currentUser?.roles.includes('it') || (currentUser?.newstaffId && specialUserIds.includes(currentUser.newstaffId))) {
      this.showEditButton = true;
    } else if (currentUser?.newstaffId) {
      // For other users, check if they have permissions for any locations
      this.locationService.getLocationOpsByStaff(currentUser.newstaffId).subscribe({
        next: (assignments) => {

          if (assignments && Array.isArray(assignments)) {
            this.showEditButton = assignments.length > 0;
          } else if (assignments) {
            this.showEditButton = true;
          } else {
            this.showEditButton = false;
          }

          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error checking location assignments:', error);
          this.showEditButton = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  private loadQuickNotes(): void {
    this.loading = true;
    this.recruitingService.getLocationQuickNotes().subscribe({
      next: (data: LocationQuickNote[]) => {
        this.ngZone.run(() => {
          this.quickNotes.set(data);
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error: any) => {
        this.ngZone.run(() => {
          this.error = 'Failed to load location quick notes';
          this.loading = false;
          this.cdr.detectChanges();
        });
        console.error('Error loading location quick notes:', error);
      }
    });
  }

  editLocationQuickNotes(): void {
    this.dataPadService.showPanel<EditLocationQuicknotesComponentInputs>(
      {
        panelName: "editLocationQuickNotes",
        inputData: {
          quickNotes: this.quickNotes()
        },
        outputs: {
          saveComplete: () => {
            this.loadQuickNotes();
          }
        }
      }
    )
  }
} 